import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { App, type AppDeps } from "./App";
import { systemClock } from "./clock";
import { LANG_CHIP_COMPACT_BELOW_PX, RECORD_DAY_SHORT_QUERY } from "./constants";
import { liveKitRoomFactory } from "./media/livekitRoom";
import { micAudioContext, microphoneOpener, openMicrophone } from "./media/mic";
import { detectIOS } from "./media/replay";
import { installMicDiag } from "./micdiag";
import { browserSaver } from "./record/summaryFile";
import { deviceDefaults } from "./data/languages";
import { loadInterface } from "./i18n";
import { readPrefs } from "./prefs";
import "./fonts.css";
import "./tokens.css";
import "./app.css";

function storage() {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

// MAJLIS-71: the `?micdiag=1` panel (micdiag.tsx). Without the flag this returns openMicrophone itself and no observer,
// and touches nothing.
const micdiag = installMicDiag({
  search: window.location.search,
  document,
  window,
  clock: systemClock,
  openMic: openMicrophone,
  observedOpener: microphoneOpener,
  audioContext: micAudioContext,
  // async: a browser with no navigator.clipboard rejects (the panel then shows the text to copy by hand), never throws
  copy: async (text) => navigator.clipboard.writeText(text),
});

// MAJLIS-109 follow-up (consultant ruling, UI-C9): built ONCE, so `chipCompact` (the initial read) and
// `chipCompactQuery` (App.tsx's live "change" listener) share the exact same MediaQueryList instead of each
// asking window.matchMedia for its own - nothing here to drift out of step with itself.
// MAJLIS-109 (UI-C9): strictly NARROWER than the bound is compact, so a viewport exactly at the bound still
// shows names (constants.ts's own derivation reads the bound as the narrowest width that clears
// MIN_SEAT_STACK_PX). The .02 keeps the query from being ambiguous with a max-width that would include it.
const chipCompactMql = window.matchMedia?.(`(max-width: ${LANG_CHIP_COMPACT_BELOW_PX - 0.02}px)`);

// MAJLIS-153 (UI-C40, consultant ruling 2): strictly narrower than the bound drops the record header's weekday.
const dayShortMql = window.matchMedia?.(RECORD_DAY_SHORT_QUERY);

const deps: AppDeps = {
  clock: systemClock,
  socket: (url) => new WebSocket(url),
  fetch: (...args) => window.fetch(...args),
  openMic: micdiag.openMic,
  media: liveKitRoomFactory(undefined, micdiag.roomObserver),
  observeSpeechStart: micdiag.speechStart, // MAJLIS-116 (T-141): undefined without the flag
  socketObserver: micdiag.socketObserver, // MAJLIS-116 (T-141): undefined without the flag
  audio: () => new Audio(),
  // MAJLIS-64 (D-43): the one persistent element spoken translations play through (a plain element: MP3 over HTTP,
  // no MediaSource, no Web Audio); separate from replay's, created once when first needed
  voiceAudio: () => new Audio(),
  voice: () => {
    // MAJLIS-62: a plain, hidden <audio> element per remote voice (never Web Audio: the echo canceller's path)
    const el = document.createElement("audio");
    el.hidden = true;
    document.body.appendChild(el);
    return el;
  },
  streamOf: (track) => new MediaStream([track]),
  isIOS: () => detectIOS(navigator),
  blobUrls: { create: (blob) => URL.createObjectURL(blob), revoke: (url) => URL.revokeObjectURL(url) },
  storage: storage(),
  deviceLangs: navigator.languages?.length ? navigator.languages : [navigator.language],
  location: window.location,
  setHash: (hash) => {
    const url = `${window.location.pathname}${window.location.search}${hash}`;
    window.history.replaceState(null, "", url);
  },
  wallNow: () => Date.now(),
  online: () => navigator.onLine !== false,
  reducedMotion: () => window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false,
  chipCompact: () => chipCompactMql?.matches ?? false,
  chipCompactQuery: () => chipCompactMql,
  dayShortQuery: () => dayShortMql,
  saveFile: browserSaver(window), // MAJLIS-154 (UI-C41): the share sheet where it takes files, else a download
  log: (message, body) => console.warn(`[majlis] ${message}`, body),
};

// MAJLIS-164 (ruling 1): the first screen's interface catalogue is fetched BEFORE the first paint, so a reader of a
// language with its own lazy chunk never sees the English one first. English (and every language still drawn in it) is
// in hand already and resolves at once; a failed fetch is logged and the app starts in English (useI18nFor retries).
const firstRead = readPrefs(deps.storage).read ?? deviceDefaults(deps.deviceLangs).read;
loadInterface(firstRead)
  .catch((err: unknown) => deps.log?.("interface catalogue did not load; starting in English", { err: String(err) }))
  .finally(() =>
    createRoot(document.getElementById("root")!).render(
      <StrictMode>
        <App deps={deps} />
      </StrictMode>,
    ),
  );

if ("serviceWorker" in navigator && import.meta.env.PROD) {
  navigator.serviceWorker.register("/sw.js");
}
